import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@parrot-software/design-system';
import { ArrowLeft, X as XIcon } from '@parrot-software/design-system/icons';

import { ParrotLogo } from '@experimental-components/IconsComponents/ParrotLogo';
import { WizardBackground } from '@subscriptions/presentation/components/base/WizardBackground';

import { WizardNavigationProvider } from './WizardNavigationContext';

type SubscriptionBlockWizardLayoutProps = {
  children: ReactNode;
  onClose: () => void;
  onBack?: () => void;
};

/**
 * Split layout for the subscription block creation wizard.
 *
 * Left panel (320px, hidden on mobile): background image with gradient overlay and Parrot logo.
 * Right panel (flex-1): wizard step content with a top chrome bar — a close (X) button anchored
 * top-right and, on mobile only (`md:hidden`), an icon-only back button anchored top-left at the
 * same `top-6` level as the X. On desktop the back action is rendered by each step's
 * `WizardStepFooter`, which consumes `onBack` through the wizard navigation context published here.
 *
 * The back button is rendered only when `onBack` is provided; the layout stays unaware of which
 * step is mounted — step routing lives in `BlockCreationWizard`.
 *
 * @param {SubscriptionBlockWizardLayoutProps} props - Component properties
 * @param {ReactNode} props.children - Step content rendered inside the right panel
 * @param {() => void} props.onClose - Handler invoked when the user clicks the X
 * @param {() => void} [props.onBack] - Handler invoked when the user clicks the back button; when omitted the back button is hidden
 */
export const SubscriptionBlockWizardLayout = ({ children, onClose, onBack }: SubscriptionBlockWizardLayoutProps) => {
  const { t } = useTranslation('subscriptions');
  const [isLocked, setIsLocked] = useState(false);
  const setLocked = useCallback((locked: boolean) => setIsLocked(locked), []);
  const navigationValue = useMemo(() => ({ onBack, setLocked }), [onBack, setLocked]);

  return (
    <div className="flex h-[100dvh] w-full font-sans">
      <div className="relative hidden h-full w-[320px] shrink-0 flex-col justify-end overflow-hidden p-8 lg:flex">
        <WizardBackground />
        <div className="absolute inset-0 bg-linear-to-b from-transparent from-36% to-black" />
        <ParrotLogo className="relative h-16" />
      </div>

      <div className="relative flex h-[100dvh] flex-1 flex-col overflow-y-auto bg-background">
        {onBack && !isLocked && (
          <Button
            aria-label={t('blockCreationWizard.backAriaLabel')}
            className="absolute left-6 top-6 z-10 md:hidden"
            onClick={onBack}
            size="icon"
            type="button"
            variant="outline"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}

        {!isLocked && (
          <Button
            aria-label="Close"
            className="absolute right-6 top-6 z-10"
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <XIcon />
          </Button>
        )}

        <WizardNavigationProvider value={navigationValue}>{children}</WizardNavigationProvider>
      </div>
    </div>
  );
};
